import Decimal from "decimal.js";

export const getAccountantProfitRequest = (projectId: number, from: string, to: string, currency: string) => ({
	method: "reports/profit",
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: { from, to, currency },
});

export interface ProfitMetrics {
	turnover: number;
	revenue: number;
	acquirerCost: number;
	profit: number;
	countOk: number;
	countFailed: number;
}

export interface ProfitBreakdownRow {
	id: number | null;
	name: string;
	metrics: ProfitMetrics;
}

export interface AccountantProfitData {
	period: { from: string; to: string };
	source: string;
	currency: string;
	totals: ProfitMetrics;
	clients: ProfitBreakdownRow[];
	channels: ProfitBreakdownRow[];
	notes: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isAmount = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isCount = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const isDate = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseMetrics = (value: unknown): ProfitMetrics | null => {
	if (!isRecord(value) || !isAmount(value.turnover) || !isAmount(value.revenue) || !isAmount(value.acquirer_cost)
		|| !isAmount(value.profit) || !isCount(value.count_ok) || !isCount(value.count_failed)) return null;
	return {
		turnover: value.turnover,
		revenue: value.revenue,
		acquirerCost: value.acquirer_cost,
		profit: value.profit,
		countOk: value.count_ok,
		countFailed: value.count_failed,
	};
};

const parseRows = (value: unknown, currency: string, type: "client" | "channel"): ProfitBreakdownRow[] | null => {
	if (!Array.isArray(value)) return null;
	const rows: ProfitBreakdownRow[] = [];
	for (const item of value) {
		if (!isRecord(item) || !isRecord(item.currencies)) return null;
		const idValue = item[type === "client" ? "client_id" : "channel_id"];
		if (idValue !== null && !isCount(idValue)) return null;
		const nameValue = item[type === "client" ? "client_name" : "name"];
		if (nameValue !== null && typeof nameValue !== "string") return null;
		const currencyValue = item.currencies[currency];
		if (currencyValue === undefined) continue;
		const metrics = parseMetrics(currencyValue);
		if (!metrics) return null;
		rows.push({ id: idValue, name: typeof nameValue === "string" && nameValue.trim() ? nameValue.trim() : "—", metrics });
	}
	return rows;
};

export const parseAccountantProfit = (value: unknown, from: string, to: string, currency: string): AccountantProfitData | null => {
	if (!isRecord(value) || !isRecord(value.period) || value.period.from !== from || value.period.to !== to
		|| !isDate(value.period.from) || !isDate(value.period.to) || typeof value.source !== "string"
		|| !isRecord(value.totals) || !isRecord(value.by_currency) || !Array.isArray(value.notes)
		|| value.notes.some(note => typeof note !== "string")) return null;
	const totals = parseMetrics(value.totals[currency]);
	const currencyTotals = parseMetrics(value.by_currency[currency]);
	const clients = parseRows(value.by_client, currency, "client");
	const channels = parseRows(value.by_channel, currency, "channel");
	if (!totals || !currencyTotals || !clients || !channels) return null;
	return { period: { from, to }, source: value.source, currency, totals, clients, channels, notes: value.notes };
};

export const formatProfitAmount = (amount: number, currency: string, locale: string) => `${new Intl.NumberFormat(locale, {
	minimumFractionDigits: 2, maximumFractionDigits: 4,
}).format(amount)} ${currency.toUpperCase()}`;

export const sumProfitMetrics = (rows: ProfitBreakdownRow[]): ProfitMetrics => {
	// Keep decimal accumulators throughout the sum; convert only the final values for display.
	const sum = (key: keyof ProfitMetrics) => rows.reduce((total, { metrics }) => total.plus(metrics[key]), new Decimal(0)).toNumber();
	return {
		turnover: sum("turnover"), revenue: sum("revenue"), acquirerCost: sum("acquirerCost"),
		profit: sum("profit"), countOk: sum("countOk"), countFailed: sum("countFailed"),
	};
};

export const profitSuccessRate = (metrics: ProfitMetrics) => {
	const total = new Decimal(metrics.countOk).plus(metrics.countFailed);
	return total.greaterThan(0) ? new Decimal(metrics.countOk).div(total).times(100).toNumber() : null;
};
